import React from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {Col, UiBox, UiImage, UiQuestionnaireLayout, UiSimpleGrid, UiText} from '~/components/elements';
import {ScrollToBottomButton} from '~/components/shared/ScrollToBottomButton';
import {PERIOD_CONTROL_MEDICATION_KEYS} from '~/constants/medical-questionnaires';
import {useInput, useNotification} from '~/hooks';
import {setNextQuestionnaire, setQuestionnaireAnswer} from '~/redux/questionnaires/slice';
import {RootState} from '~/redux/root-reducer';
import {AppDispatch} from '~/redux/root-store';
import {spacing} from '~/utils/theme/spacing';

const Questionnaire5Screen = () => {
  const dispatch: AppDispatch = useDispatch();
  const {addMessage} = useNotification(dispatch);

  const currentAnswer = useSelector(
    (state: RootState) =>
      state.questionnaires?.collectData[PERIOD_CONTROL_MEDICATION_KEYS.HAD_BLOOD_PRESSURE_MEASURED_LAST_6_MONTH]?.data,
  );

  const {inputs, setInputs} = useInput({
    selectedOption: currentAnswer?.selectedOption || '',
    selectedBlood: currentAnswer?.selectedBlood || '',
  });

  const medicalQuestionnaire = useSelector((state: RootState) => state.questionnaires.medicalQuestionnaire);

  const questionnaires = medicalQuestionnaire?.questionnaires;
  const questionnaire = questionnaires?.find(
    i => i.key === PERIOD_CONTROL_MEDICATION_KEYS.HAD_BLOOD_PRESSURE_MEASURED_LAST_6_MONTH,
  );

  const selectedOptionData = questionnaire?.options?.find(i => i.value === inputs.selectedOption);

  const onClickSubmit = async () => {
    dispatch(
      setQuestionnaireAnswer({
        key: PERIOD_CONTROL_MEDICATION_KEYS.HAD_BLOOD_PRESSURE_MEASURED_LAST_6_MONTH,
        index: questionnaire?.index || '',
        data: {...inputs, selectedBlood: inputs.selectedOption === 'YES' ? inputs.selectedBlood : ''},
      }),
    );
    dispatch(setNextQuestionnaire({value: PERIOD_CONTROL_MEDICATION_KEYS.DO_SMOKE}));
  };

  const renderDesc = () => {
    if (inputs.selectedOption) {
      return (
        <UiBox w="100%" mt={spacing.small}>
          {selectedOptionData?.relations?.map(i => {
            if (i?.elementType?.type === 'QUESTION') {
              return (
                <UiBox
                  w="100%"
                  mt={spacing.small}
                  key={i?.key || i?.elementType?.type}
                  className="animate__animated animate__fadeInLeft"
                >
                  <UiText fontSize="md" fontWeight="bold" mt={spacing.small}>
                    {i?.questions?.default}
                  </UiText>
                  <UiSimpleGrid columns={{base: 1, lg: 2}} spacing={5} mt={spacing.small}>
                    {i?.options?.map(value => (
                      <ScrollToBottomButton
                        key={`${value.label}-${value.value}`}
                        title={value.label}
                        variant={inputs.selectedBlood === value.value ? 'solid' : 'toggle'}
                        onClick={() => setInputs({selectedBlood: value.value})}
                      />
                    ))}
                  </UiSimpleGrid>
                </UiBox>
              );
            }
            if (i?.elementType?.type === 'DESCRIPTION') {
              return (
                <UiBox
                  w="100%"
                  mt={spacing.small}
                  key={i?.key || i?.elementType?.type}
                  className="animate__animated animate__fadeInLeft"
                >
                  <UiText fontSize="md" fontWeight="bold" mt={spacing.small}>
                    {i?.questions?.default}
                  </UiText>
                  {i?.descriptions?.default?.map(des => (
                    <UiText fontSize="sm" mt={spacing.small} key={des}>
                      {des}
                    </UiText>
                  ))}
                </UiBox>
              );
            }
            return null;
          })}
        </UiBox>
      );
    }
    return null;
  };

  return (
    <UiQuestionnaireLayout
      actions={questionnaire?.actions}
      disableNext={!inputs.selectedOption || (inputs.selectedOption === 'YES' && !inputs.selectedBlood)}
      handleOnNext={onClickSubmit}
      checkoutStep={questionnaire?.checkoutStep}
    >
      <Col alignItems="center" textAlign="center">
        <UiText fontSize="xl" fontWeight="bold" mt={spacing.large}>
          {questionnaire?.questions?.default}
        </UiText>
        <UiBox mt={spacing.medium} w="100%">
          <UiSimpleGrid columns={2} spacing={5}>
            {questionnaire?.options?.map(value => (
              <ScrollToBottomButton
                key={`${value.label}-${value.value}`}
                title={value.label}
                variant={inputs.selectedOption === value.value ? 'solid' : 'toggle'}
                onClick={() => setInputs({selectedOption: value.value})}
              />
            ))}
          </UiSimpleGrid>
        </UiBox>
        {renderDesc()}
      </Col>
    </UiQuestionnaireLayout>
  );
};

export default Questionnaire5Screen;
